import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Card, Spinner, Button, Row, Col, Badge, Table } from "react-bootstrap";
import { authService } from "../services/authService";
import { orderService } from "../services/orderService";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import TrackingMap from "../components/Tracking/TrackingMap";
import api from '../config/ApiConfig';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const history = useHistory();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [podImages, setPodImages] = useState([]);
  const [loadingPodImages, setLoadingPodImages] = useState(false);
  const currentUser = useMemo(() => authService.getCurrentUser(), []);

  const redirectToHistory = useCallback(() => {
    history.push('/historique');
  }, [history]);

  useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        setLoading(true);
        // Use orderService to benefit from cache
        const data = await orderService.getOrderHistory();
        const ordersArray = data.data || data;
        const foundOrder = ordersArray.find(o => o.id === parseInt(orderId));

        if (foundOrder) {
          setOrder(foundOrder);
          // Log pour debug (optionnel - peut être retiré en production)
          console.log('📦 Commande #' + foundOrder.id + ' chargée');
          console.log('📍 StatuCommande:', foundOrder.StatuCommande);
          console.log('🔑 tracking_token:', foundOrder.tracking_token);
          if (foundOrder.Bordereau) {
            console.log('📄 Bordereau disponible:', foundOrder.Bordereau);
          }

          // Si la commande est livrée (StatuCommande === 2), charger les images POD
          if (foundOrder.StatuCommande === 2) {
            fetchPodImages(foundOrder.id);
          }
        } else {
          // Order not found, redirect back to history
          redirectToHistory();
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des détails:", error);
        redirectToHistory();
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.id && orderId) {
      fetchOrderDetail();
    }
  }, [orderId, currentUser, redirectToHistory]);

  // Fonction pour récupérer les images POD
  const fetchPodImages = async (commandeId) => {
    try {
      setLoadingPodImages(true);
      const data = await orderService.getPodImages(commandeId);
      if (data.images && data.images.length > 0) {
        setPodImages(data.images);
        console.log('📷 Images POD chargées:', data.images.length);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des images POD:", error);
      // Ne pas bloquer l'affichage si les images POD échouent
      setPodImages([]);
    } finally {
      setLoadingPodImages(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour obtenir la date d'une étape spécifique
  const getStepDate = (step) => {
    if (!order) return null;

    switch (step) {
      case 'validation':
        // Date de validation = DateCommande (quand la commande est créée)
        return order.DateCommande || order.created_at;
      case 'preparation':
        // Date de préparation = utiliser updated_at si StatuCommande >= 3
        return order.StatuCommande >= 3 ? order.updated_at : null;
      case 'livraison':
        // Date de début de livraison = DateLivraison prévue
        return order.StatuCommande >= 1 ? order.DateLivraison : null;
      case 'livre':
        // Date de livraison réelle (quand c'est effectivement livré)
        return order.DateLivraisonReelle;
      default:
        return null;
    }
  };

  const getStatusLabel = (statutCommande, statutPaiement) => {
    const statusMap = {
      1: { label: 'En cours de livraison', variant: 'primary' },
      2: { label: 'Livré', variant: 'success' },
      3: { label: 'En cours de préparation', variant: 'info' },
      4: { label: 'En cours de validation', variant: 'warning' },
      6: { label: 'Annulé', variant: 'danger' }
    };

    const status = statusMap[statutCommande] || { label: 'En attente', variant: 'secondary' };

    if (statutPaiement === 1 || statutPaiement === '1') {
      return { label: `${status.label} - Payé`, variant: status.variant };
    } else if (statutPaiement === 0 || statutPaiement === '0') {
      return { label: `${status.label} - Non payé`, variant: 'warning' };
    }

    return status;
  };

  const handleDownloadInvoice = () => {
    try {
      const invoiceFileName = order?.NumeroFacture;

      if (!invoiceFileName) {
        console.error("Aucune facture disponible pour cette commande");
        return;
      }

      console.log('📄 Nom fichier facture:', invoiceFileName);

      let baseUrl = api.defaults.baseURL;

      if (baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.slice(0, -4);
      } else if (baseUrl.endsWith('/api/')) {
        baseUrl = baseUrl.slice(0, -5);
      }

      baseUrl = baseUrl.replace(/\/+$/, '');

      const invoiceUrl = `${baseUrl}/factures/${invoiceFileName}`;

      console.log('📄 URL complète facture:', invoiceUrl);
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error("Erreur lors du téléchargement de la facture:", error);
    }
  };


  const handleDownloadBordereau = () => {
    // Le champ s'appelle "Bordereau" (avec majuscule)
    const bordereauUrl = order?.Bordereau;

    if (bordereauUrl) {
      console.log('📄 Ouverture du bordereau:', bordereauUrl);
      console.log('📄 Base URL:', api.defaults.baseURL);

      // Construire l'URL complète
      let fullUrl;
      if (bordereauUrl.startsWith('http')) {
        // URL absolue, l'utiliser telle quelle
        fullUrl = bordereauUrl;
      } else {
        // URL relative - le dossier Bordereau est dans public/, accessible depuis la racine
        // Construction robuste de l'URL
        let baseUrl = api.defaults.baseURL;

        // Retirer /api à la fin
        if (baseUrl.endsWith('/api')) {
          baseUrl = baseUrl.slice(0, -4);
        } else if (baseUrl.endsWith('/api/')) {
          baseUrl = baseUrl.slice(0, -5);
        }

        // S'assurer que baseUrl ne se termine pas par /
        baseUrl = baseUrl.replace(/\/+$/, '');

        // S'assurer que bordereauUrl ne commence pas par /
        const cleanBordereauUrl = bordereauUrl.replace(/^\/+/, '');

        fullUrl = `${baseUrl}/${cleanBordereauUrl}`;
      }

      console.log('📄 URL complète du bordereau:', fullUrl);
      window.open(fullUrl, '_blank');
    } else {
      console.error('Aucun bordereau trouvé pour cette commande');
    }
  };

  // Fonction pour déterminer si une étape de la timeline est active
  const isTimelineStepActive = (step, currentStatus) => {
    // Si annulé, seule la première étape (validation) est active
    if (currentStatus === 6) {
      return step === 'validation';
    }

    // Progression normale : 4 -> 3 -> 1 -> 2
    const statusProgression = {
      validation: [4, 3, 1, 2], // Active si statut est 4, 3, 1 ou 2
      preparation: [3, 1, 2],   // Active si statut est 3, 1 ou 2
      livraison: [1, 2],        // Active si statut est 1 ou 2
      livre: [2]                // Active si statut est 2
    };

    return statusProgression[step]?.includes(currentStatus) || false;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="container mt-5 mb-5">
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!order) {
    return (
      <>
        <Header />
        <div className="container mt-5 mb-5">
          <Card className="text-center p-5">
            <Card.Body>
              <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <p className="text-muted">Commande introuvable</p>
              <Button variant="primary" onClick={() => history.push('/historique')}>
                Retour à l'historique
              </Button>
            </Card.Body>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  const status = getStatusLabel(order.StatuCommande, order.StatuPaiement);

  return (
    <>
      <Header />
      <div className="container mt-5 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button variant="outline-secondary" size="sm" onClick={() => history.push('/historique')}>
              <i className="fas fa-arrow-left me-2"></i>
              Retour
            </Button>
            <h2 className="mt-3 mb-0">Détails de la commande #{order.id}</h2>
          </div>
          <Badge bg={status.variant} className="px-3 py-2">
            {status.label}
          </Badge>
        </div>

        <Row>
          <Col md={8}>
            {/* Tracking Map - Show only for orders in delivery (StatuCommande === 1) */}
            {order.StatuCommande === 1 && (
              <TrackingMap orderId={order.id} />
            )}

            {/* Order Information */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Informations de la commande
                </h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: '40%' }}>
                        <i className="fas fa-calendar me-2"></i>
                        Date de commande
                      </td>
                      <td className="fw-bold">{formatDate(order.DateCommande || order.created_at)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">
                        <i className="fas fa-hashtag me-2"></i>
                        Numéro de commande
                      </td>
                      <td className="fw-bold">#{order.id}</td>
                    </tr>
                    {order.RaisonSocialClient && (
                      <tr>
                        <td className="text-muted">
                          <i className="fas fa-building me-2"></i>
                          Raison sociale
                        </td>
                        <td className="fw-bold">{order.RaisonSocialClient}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Adresse de livraison
                      </td>
                      <td className="fw-bold">{order.AdresseLivraison}</td>
                    </tr>
                    {order.poids_label && (
                      <tr>
                        <td className="text-muted">
                          <i className="fas fa-weight me-2"></i>
                          Poids
                        </td>
                        <td className="fw-bold">{order.poids_label}</td>
                      </tr>
                    )}
                    {order.email && (
                      <tr>
                        <td className="text-muted">
                          <i className="fas fa-envelope me-2"></i>
                          Email
                        </td>
                        <td className="fw-bold">{order.email}</td>
                      </tr>
                    )}
                    {order.telephone && (
                      <tr>
                        <td className="text-muted">
                          <i className="fas fa-phone me-2"></i>
                          Téléphone
                        </td>
                        <td className="fw-bold">{order.telephone}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Pricing Information */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-euro-sign me-2"></i>
                  Informations tarifaires
                </h5>
              </Card.Header>
              <Card.Body>
                <Table borderless>
                  <tbody>
                    {order.HT && (
                      <tr>
                        <td className="text-muted">Montant HT</td>
                        <td className="text-end fw-bold">{parseFloat(order.HT).toFixed(2)} €</td>
                      </tr>
                    )}
                    {order.TVA && (
                      <tr>
                        <td className="text-muted">TVA</td>
                        <td className="text-end fw-bold">{parseFloat(order.TVA).toFixed(2)} €</td>
                      </tr>
                    )}
                    <tr className="border-top">
                      <td className="text-success fw-bold h5 mb-0">Total TTC</td>
                      <td className="text-end text-success fw-bold h5 mb-0">
                        {parseFloat(order.TTC || order.tarif_ttc || 0).toFixed(2)} €
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* Delivery Note / Bon de livraison */}
            {order.Bordereau && (
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-file-alt me-2"></i>
                    Bon de livraison
                  </h5>
                </Card.Header>
                <Card.Body>
                  <div className="text-center py-3">
                    <i className="fas fa-file-pdf fa-4x text-danger mb-3"></i>
                    <p className="text-muted mb-3">
                      Bon de livraison uploadé par le client
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleDownloadBordereau}
                      className="mb-2"
                    >
                      <i className="fas fa-download me-2"></i>
                      Télécharger le bon de livraison
                    </Button>
                    <div className="mt-3">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleDownloadBordereau}
                      >
                        <i className="fas fa-eye me-2"></i>
                        Ouvrir dans un nouvel onglet
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Proof of Delivery / Preuve de livraison */}
            {order.StatuCommande === 2 && podImages.length > 0 && (
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-camera me-2"></i>
                    Preuve de livraison
                  </h5>
                </Card.Header>
                <Card.Body>
                  {loadingPodImages ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                      <p className="text-muted mt-2">Chargement des images...</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Photos de preuve de livraison ({podImages.length} image{podImages.length > 1 ? 's' : ''})
                      </p>
                      <Row>
                        {podImages.map((image, index) => (
                          <Col xs={12} sm={6} md={4} key={image.id} className="mb-3">
                            <div className="pod-image-container">
                              <img
                                src={image.image_url}
                                alt={`Preuve de livraison ${index + 1}`}
                                className="img-fluid rounded shadow-sm"
                                style={{
                                  width: '100%',
                                  height: '200px',
                                  objectFit: 'cover',
                                  cursor: 'pointer'
                                }}
                                onClick={() => window.open(image.image_url, '_blank')}
                              />
                              <div className="mt-2">
                                <small className="text-muted d-block">
                                  <i className="fas fa-clock me-1"></i>
                                  {image.uploaded_at}
                                </small>
                                {image.description && (
                                  <small className="text-muted d-block mt-1">
                                    {image.description}
                                  </small>
                                )}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <div className="text-center mt-3">
                        <small className="text-muted">
                          <i className="fas fa-hand-pointer me-1"></i>
                          Cliquez sur une image pour l'agrandir
                        </small>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>

          <Col md={4}>
            {/* Actions */}
            <Card className="mb-4 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-cog me-2"></i>
                  Actions
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  {order.StatuPaiement === 3 && (
                    <Button
                      variant="success"
                      onClick={handleDownloadInvoice}
                    >
                      <i className="fas fa-file-invoice me-2"></i>
                      Télécharger la facture
                    </Button>
                  )}
                  {order.bordereau_url && (
                    <Button
                      variant="primary"
                      onClick={handleDownloadBordereau}
                    >
                      <i className="fas fa-file-pdf me-2"></i>
                      Voir le bordereau
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={() => history.push('/historique')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Retour à l'historique
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Status Timeline */}
            <Card className="shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Statut
                </h5>
              </Card.Header>
              <Card.Body>
                {order.StatuCommande === 6 ? (
                  // Affichage spécial pour les commandes annulées
                  <div className="text-center py-3">
                    <i className="fas fa-times-circle fa-3x text-danger mb-3"></i>
                    <h5 className="text-danger">Commande annulée</h5>
                    <p className="text-muted small">
                      Cette commande a été annulée et ne sera pas traitée.
                    </p>
                  </div>
                ) : (
                  <div className="timeline">
                    <div className={`timeline-item ${isTimelineStepActive('validation', order.StatuCommande) ? 'active' : ''}`}>
                      <i className="fas fa-check-circle"></i>
                      <div>
                        <div>En cours de validation</div>
                        {isTimelineStepActive('validation', order.StatuCommande) && getStepDate('validation') && (
                          <small className="text-muted d-block mt-1">
                            {formatTime(getStepDate('validation'))}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className={`timeline-item ${isTimelineStepActive('preparation', order.StatuCommande) ? 'active' : ''}`}>
                      <i className="fas fa-box"></i>
                      <div>
                        <div>En cours de préparation</div>
                        {isTimelineStepActive('preparation', order.StatuCommande) && getStepDate('preparation') && (
                          <small className="text-muted d-block mt-1">
                            {formatTime(getStepDate('preparation'))}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className={`timeline-item ${isTimelineStepActive('livraison', order.StatuCommande) ? 'active' : ''}`}>
                      <i className="fas fa-truck"></i>
                      <div>
                        <div>En cours de livraison</div>
                        {isTimelineStepActive('livraison', order.StatuCommande) && getStepDate('livraison') && (
                          <small className="text-muted d-block mt-1">
                            {formatTime(getStepDate('livraison'))}
                          </small>
                        )}
                      </div>
                    </div>
                    <div className={`timeline-item ${isTimelineStepActive('livre', order.StatuCommande) ? 'active' : ''}`}>
                      <i className="fas fa-home"></i>
                      <div>
                        <div>Livré</div>
                        {isTimelineStepActive('livre', order.StatuCommande) && getStepDate('livre') && (
                          <small className="text-muted d-block mt-1">
                            {formatTime(getStepDate('livre'))}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <Footer />

      <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        .timeline-item {
          position: relative;
          padding: 15px 0;
          color: #999;
        }
        .timeline-item.active {
          color: #0d6efd;
          font-weight: 600;
        }
        .timeline-item i {
          position: absolute;
          left: -30px;
          top: 15px;
        }
        .timeline-item:not(:last-child)::before {
          content: '';
          position: absolute;
          left: -23px;
          top: 30px;
          width: 2px;
          height: 100%;
          background: #ddd;
        }
        .timeline-item.active:not(:last-child)::before {
          background: #0d6efd;
        }
      `}</style>
    </>
  );
};

export default OrderDetailPage;
